// We're relying on @react-aria to make appropriate choices re: accessibility here; this is based
// closely on examples from their docs
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { useSlider } from '@react-aria/slider';
import { useNumberFormatter } from '@react-aria/i18n';

import { getSliderNumberFormatConfig } from '@trqk/utils';

import { SliderBody } from './components';
import { useSliderStateWithDiscreteSteps } from './utils';
import {
    defaultInputWidth,
    defaultMaxValue,
    defaultMinValue,
    defaultWidth,
} from './constants';

interface SliderWithDiscreteStepsProps {
    /**
     * Optional default step index.
     * @default 0
     */
    defaultStepIndex?: number;
    /**
     * A label for the slider
     */
    label?: string;
    /**
     * Optional class config which will override the default border classes on the input
     */
    inputClassConfig?: InputClassConfig;
    /**
     * Optional CSS classes for the label on the input
     */
    inputLabelClasses?: string;
    /**
     * Width for the <input> element
     * @defaultValue 130
     */
    inputWidth?: number;
    /**
     * How to format numbers for both the current value label and the min/max labels
     */
    numberFormat: SliderNumberFormat;
    /**
     * Method called with the next value when it changes
     */
    onChange: (value: number[]) => void;
    /**
     * A discrete list of steps that the user will be allowed to select from.
     */
    steps: number[];
    /**
     * The rounding type to use when a user enters a number that is not one of the listed `steps`.
     * See {@link https://github.com/trqk/trqk-client/blob/develop/packages/components/src/Slider/utils/getStepValue.ts | getStepValue}
     */
    roundType: SliderStepRoundType;
    /**
     * Overall width; slider track will be smaller than this to allow for width of the thumb when
     * in leftmost or rightmost position
     * @default 250
     */
    width?: number;
}

/**
 * An accessible slider component built from React Aria libraries, based on this example:
 * {@link https://react-spectrum.adobe.com/react-aria/useSlider.html#useslider-hook}
 *
 * This version expects a `steps` prop that includes all steps the user will be allowed to select.
 * If the user tries to set the thumb somewhere between steps, it will be rounded up or down to the
 * nearest step value when they release the thumb.
 *
 * This is built on `useSliderStateWithDiscreteSteps`, which is a wrapper around `useSliderState`
 * from @react-stately/slider. `useSliderState` supports multiple values for a single slider in
 * order to represent a range with a slider, so the value will be formatted here as a number[]. This
 * component only supports a single value, however, so the number[] will only contain one element.
 * @param props - {@link SliderWithDiscreteStepsProps}
 */
export function SliderWithDiscreteSteps(
    props: SliderWithDiscreteStepsProps,
): JSX.Element {
    const {
        defaultStepIndex = 0,
        inputClassConfig,
        inputLabelClasses,
        inputWidth = defaultInputWidth,
        label,
        numberFormat,
        roundType,
        steps,
        width = defaultWidth,
    } = props;

    const defaultValue = [steps[defaultStepIndex] ?? 0];
    const [value, setValue] = useState(defaultValue);
    const onChange = (valueList: number[]): void => {
        setValue(valueList);
        props.onChange(valueList);
    };
    const { value: valueFormatOptions } =
        getSliderNumberFormatConfig(numberFormat);
    const trackRef = React.useRef(null);
    const valueFormatter = useNumberFormatter(valueFormatOptions);
    const state = useSliderStateWithDiscreteSteps({
        defaultValue,
        label,
        numberFormatter: valueFormatter,
        onChange,
        roundType,
        steps,
        value,
    });
    const maxValue = steps[steps.length - 1] ?? defaultMaxValue;
    const minValue = steps[0] ?? defaultMinValue;

    const { groupProps, trackProps, labelProps, outputProps } = useSlider(
        {
            defaultValue,
            label,
            maxValue,
            minValue,
            step: 1,
        },
        state,
        trackRef,
    );

    return (
        <SliderBody
            defaultValue={defaultValue}
            inputClassConfig={inputClassConfig}
            inputLabelClasses={inputLabelClasses}
            inputWidth={inputWidth}
            groupProps={groupProps}
            label={label}
            labelProps={labelProps}
            maxValue={maxValue}
            minValue={minValue}
            numberFormat={numberFormat}
            onChange={onChange}
            outputProps={outputProps}
            roundType={roundType}
            state={state}
            steps={steps}
            trackProps={trackProps}
            trackRef={trackRef}
            width={width}
        />
    );
}
